import type { ProjectGate } from '@bpd/shared';

const VALUE_LABEL: Record<ProjectGate['value'], string> = {
  yes: 'Yes',
  no: 'No',
  'n-a': 'N/A',
};

const VALUE_CLS: Record<ProjectGate['value'], string> = {
  yes: 'bg-green-100 text-green-800',
  no: 'bg-gray-100 text-gray-600',
  'n-a': 'bg-gray-50 text-gray-400 border border-dashed border-gray-300',
};

/** Short label per gate for compact mode, where the full label + "Yes"/"No" doesn't fit on a
 * card row — the badge's color already carries the yes/no/n-a signal. */
const COMPACT_LABEL: Record<string, string> = {
  scoped: 'Scope',
  estimateActive: 'Estimate',
  contractorSelected: 'Contractor',
  workStarted: 'Started',
  workComplete: 'Completed',
  invoiced: 'Invoiced',
  paidInFull: 'Paid',
  closed: 'Closed',
};

interface GateBadgesProps {
  gates: ProjectGate[];
  /** 'full': one tile per gate with its badge + detail caption, for ProjectDetailPage. 'compact':
   * a single wrapping row of name+badge chips with the detail in a tooltip, for list rows
   * (ClientsPage/ClientDetailPage) where space is tight. */
  mode: 'compact' | 'full';
}

/** Renders every independent project gate (see deriveProjectGates in @bpd/shared) as its own
 * badge — deliberately never collapsed into one status label, so e.g. "Paid in full: yes" and
 * "Work complete: no" can both be visible at once instead of one hiding the other. */
export function GateBadges({ gates, mode }: GateBadgesProps) {
  if (mode === 'compact') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {gates.map((g) => (
          <span
            key={g.key}
            title={`${g.label}: ${VALUE_LABEL[g.value]} — ${g.detail}`}
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${VALUE_CLS[g.value]}`}
          >
            {COMPACT_LABEL[g.key] ?? g.label}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 sm:grid-cols-4">
      {gates.map((g) => (
        <div key={g.key} className="flex flex-col items-start gap-1 bg-white p-3">
          <span
            className={`inline-block w-fit rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${VALUE_CLS[g.value]}`}
          >
            {VALUE_LABEL[g.value]}
          </span>
          <span className="text-sm font-medium text-gray-900">{g.label}</span>
          <span className="text-xs text-gray-500">{g.detail}</span>
        </div>
      ))}
    </div>
  );
}

/** True when a project's money and work facts disagree — paid in full but not every task is
 * marked complete. The whole point of independent gates over one derived label: this is shown
 * directly rather than smoothed into a single "in progress" or "closed" word. */
export function hasPaidButIncompleteMismatch(gates: ProjectGate[]): boolean {
  const paid = gates.find((g) => g.key === 'paidInFull');
  const complete = gates.find((g) => g.key === 'workComplete');
  return paid?.value === 'yes' && complete?.value === 'no';
}
